import { API_ROOT } from '../../config';
import { buildDictionaries, MOCK_STORE } from './mockData';
import { computePeriodPreview } from './periodUtils';
import { getNetwork } from '../../utils/getNetwork';

export const USE_MOCK = true;

const BASE = `${API_ROOT}/api/admin/analytics-indicators`;
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

const FIELD_MAP = {
  indicator_id: 'indicator',
  period_type: 'ptype',
  period_year: 'period',
  period_index: 'period',
  value: 'value',
};

export function normalizeApiError(error) {
  const status = error && error.response ? error.response.status : 0;
  const data = error && error.response ? error.response.data : null;

  if (status === 403)
    return { status, forbidden: true, fields: [], messageKey: 'analyticsIndicators.err.forbidden' };

  if (status === 409) {
    return {
      status,
      fields: [{ field: 'period', messageKey: 'analyticsIndicators.err.dup' }],
      messageKey: null,
    };
  }

  if (status === 422) {
    const raw = data && Array.isArray(data.fields) ? data.fields : [];
    const fields = raw.map((f) => ({
      field: FIELD_MAP[f.field] || f.field,
      message: f.message || null,
      messageKey: FIELD_MAP[f.field] ? null : null,
    }));
    return { status, fields, messageKey: fields.length ? null : 'analyticsIndicators.err.save' };
  }

  return { status, fields: [], messageKey: 'analyticsIndicators.err.save' };
}

export async function getDictionaries() {
  if (USE_MOCK) {
    await delay(120);
    return buildDictionaries();
  }
  const net = getNetwork();
  const { data } = await net.get(`${BASE}/dictionaries`);
  return data;
}

export async function getValues(params = {}) {
  const {
    page = 1,
    size = 100,
    search = '',
    indicatorId = null,
    periodType = null,
    dateFrom = null,
    dateTo = null,
  } = params;

  if (USE_MOCK) {
    await delay(120);
    let rows = MOCK_STORE.rows.slice();
    if (indicatorId) rows = rows.filter((r) => r.indicator_id === indicatorId);
    if (periodType) rows = rows.filter((r) => r.period_type === periodType);
    if (dateFrom) rows = rows.filter((r) => r.period_end >= dateFrom);
    if (dateTo) rows = rows.filter((r) => r.period_start <= dateTo);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          `${r.period_label_ru} ${r.period_label_kk}`.toLowerCase().includes(q) ||
          String(r.indicator_id).includes(q)
      );
    }
    const total = rows.length;
    const start = (page - 1) * size;
    return { rows: rows.slice(start, start + size), total, page, size };
  }

  const net = getNetwork();
  const query = { page, size };
  if (search) query.search = search;
  if (indicatorId) query.indicator_id = indicatorId;
  if (periodType) query.period_type = periodType;
  if (dateFrom) query.date_from = dateFrom;
  if (dateTo) query.date_to = dateTo;
  const { data } = await net.get(`${BASE}/values`, { params: query });
  return data;
}

export async function getValue(id) {
  if (USE_MOCK) {
    await delay(80);
    return MOCK_STORE.rows.find((r) => r.id === id) || null;
  }
  const net = getNetwork();
  const { data } = await net.get(`${BASE}/values/${id}`);
  return data;
}

export async function createValue(payload, ctx) {
  const body = {
    indicator_id: payload.indicator_id,
    period_type: payload.period_type,
    period_year: payload.period_year,
    period_index: payload.period_type === 'year' ? null : payload.period_index,
    value: payload.value,
  };

  if (USE_MOCK) {
    await delay(200);
    const it = (ctx && ctx.indicator) || null;
    if (it) {
      const preview = computePeriodPreview(
        {
          indicator: it,
          periodType: body.period_type,
          periodYear: body.period_year,
          periodIndex: body.period_index,
        },
        'ru'
      );
      const dup = MOCK_STORE.rows.some(
        (r) => r.indicator_id === body.indicator_id && r.period_end === preview.period_end
      );
      if (dup) {
        const e = new Error('duplicate');
        e.response = { status: 409, data: {} };
        throw e;
      }

      const row = {
        id: MOCK_STORE.nextId++,
        indicator_id: body.indicator_id,
        period_type: body.period_type,
        period_year: body.period_year,
        period_index: body.period_index,
        value: Number(
          String(body.value)
            .replace(/[\s\u00A0]/g, '')
            .replace(',', '.')
        ),
        period_start: preview.period_start,
        period_end: preview.period_end,
        period_label_ru: preview.label_ru,
        period_label_kk: preview.label_kk,
        created_at: new Date().toISOString().slice(0, 16),
        created_by_ru: 'Оператор модулей',
        created_by_kk: 'Модульдер операторы',
      };
      MOCK_STORE.rows.unshift(row);
      return row;
    }
  }

  const net = getNetwork();
  const { data } = await net.post(`${BASE}/values`, body);
  return data;
}
